/**
 * The roster (C15) from `GET /v1/videos/{id}/players`: the batsman and bowler
 * groups the pipeline's player-identity stage found in this video, in the order
 * they first appear, each with ONE picture. A group is a suggestion shown by
 * the stage's label ("Batsman 3") until a user names it. Before the stage has
 * run, while it runs, or when it could not, the roster is empty and `identity`
 * says which; nothing here invents players. A user may merge two players the
 * stage split: the API then shows one player with both players' clips, and
 * `merges` is the stack Undo merge takes back, newest first.
 */
import type { Clip, Identity, Pair, Player, PlayerMerge, Roster, RosterAssignmentInfo } from "../api/types";
import { plural } from "./format";

export interface ResolvedRoster {
  /** null while the roster is loading */
  identity: Identity | null;
  batsmen: Player[];
  bowlers: Player[];
  pairs: Pair[];
  /** the live merges, newest first: what Undo merge takes back, in turn */
  merges: PlayerMerge[];
  /** @handle assignment metadata for the video */
  assignment: RosterAssignmentInfo | null;
  /** the player an id shows as: itself, or for a merged player (a link from before the merge) the one it joined */
  shown: (id: string | null) => string | null;
  /** a clip's two players (null: not identified, or that role was not picked in it) */
  assign: (clip: Clip) => { batsman_id: string | null; bowler_id: string | null };
  /** the @handle or name a user typed, else the stage's label; "Any" for no player */
  name: (id: string | null) => string;
  /** the stage's label ("Batsman 3"), whatever the player was named; "Any" for no player */
  label: (id: string | null) => string;
}

/** The stage's label for a player: "Batsman 3", "Bowler 1". */
export function playerLabel(p: Pick<Player, "role" | "order">): string {
  return `${p.role === "batsman" ? "Batsman" : "Bowler"} ${p.order}`;
}

export function resolveRoster(api: Roster | null): ResolvedRoster {
  const players = api?.players ?? [];
  const byId = new Map(players.map((p) => [p.id, p]));
  const merges = api?.merges ?? [];
  const into = new Map(merges.map((m) => [m.player.id, m.into.id]));
  const shown = (id: string | null) => {
    let r = id;
    for (let n = 0; r && into.has(r) && n <= merges.length; n++) r = into.get(r)!;
    return r;
  };
  const find = (id: string | null) => {
    const r = shown(id);
    return r ? byId.get(r) : undefined;
  };
  return {
    identity: api?.identity ?? null,
    batsmen: players.filter((p) => p.role === "batsman").sort((a, b) => a.order - b.order),
    bowlers: players.filter((p) => p.role === "bowler").sort((a, b) => a.order - b.order),
    pairs: api?.pairs ?? [],
    merges,
    assignment: api?.assignment ?? null,
    shown,
    assign: (c) => ({ batsman_id: c.batsman_id, bowler_id: c.bowler_id }),
    name: (id) => {
      const p = find(id);
      if (!p) return "Any";
      if (p.assignee?.handle) return `@${p.assignee.handle}`;
      return p.name || playerLabel(p);
    },
    label: (id) => {
      const p = find(id);
      return p ? playerLabel(p) : "Any";
    },
  };
}

/** The stage is queued or running: the page polls until it is not. */
export function identityBusy(r: ResolvedRoster): boolean {
  return r.identity?.state === "queued" || r.identity?.state === "running";
}

const UNAVAILABLE: Record<string, string> = {
  NO_SIZING_P: "The stumps could not be measured in this video, so the players cannot be found.",
  NO_STRIP: "The camera does not show where the batsman and the bowler stand.",
  NO_PICKS: "No batsman or bowler could be picked out in this video.",
  NO_DELIVERIES: "There are no deliveries to find players in.",
  TOO_FEW_PAIRS: "Too few deliveries to tell the players apart.",
  BATTING_END_REVERSED: "This analysis has the two ends of the pitch the wrong way round, so the players cannot be found in it.",
  NO_CUDA: "This analysis ran without the graphics card that finding players needs.",
  // the API's region rule (apps/api/src/services/region.ts); plain words, no mention of laws
  NOT_OFFERED: "Player cards aren't available for this session. Every ball, the pitch map, speeds and clips are.",
};

/** One plain line about the roster's state, or null when there is nothing to add. */
export function identityLine(r: ResolvedRoster, selectable: boolean): string | null {
  const s = r.identity;
  if (!s) return null;
  switch (s.state) {
    case "ready": {
      const parts: string[] = [];
      if (s.unassigned?.batsman) parts.push(`${plural(s.unassigned.batsman, "clip")} with no batsman`);
      if (s.unassigned?.bowler) parts.push(`${plural(s.unassigned.bowler, "clip")} with no bowler`);
      return parts.length ? `${parts.join(" and ")} found: those stay in the whole match.` : null;
    }
    case "queued":
    case "running":
      return "Finding the players. This takes a few minutes after the clips are ready.";
    case "failed":
      return "Finding the players did not work this time.";
    case "not_run":
      return selectable ? "The players have not been found for this video yet." : "Players are found on Basic.";
    case "unavailable":
      return UNAVAILABLE[s.reason ?? ""] ?? "The players could not be found in this video.";
    case "off":
      return "Finding players is switched off.";
  }
}

export function countFor(r: ResolvedRoster, batsman: string | null, bowler: string | null, clips: Clip[]): number {
  return clips.filter((c) => {
    if (c.flagged) return false;
    const a = r.assign(c);
    return (!batsman || a.batsman_id === batsman) && (!bowler || a.bowler_id === bowler);
  }).length;
}
